import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {GenderSelect} from '@client/components/GenderSelect';
import {StateSelect} from '@client/components/StateSelect';
import {
  VIP_TYPE_LABELS,
  type VipFormMode,
  type VipFormValues,
  type VipType,
} from '@client/pages/vip/vipForm';

type VipFormChange = (patch: Partial<VipFormValues>) => void;

type VipFieldBlockProps = {
  onChange: VipFormChange;
  values: VipFormValues;
  variant: VipFormMode;
};

type VipFieldLayout = {
  cityStateZipGrid: string;
  contactGrid: string;
  detailsGrid: string;
  email: {placeholder?: string};
  identityGrid: string;
  ids: {
    address1: string;
    address2: string;
    city: string;
    dateOfBirth: string;
    email: string;
    firstName: string;
    gender: string;
    lastName: string;
    phoneNumber: string;
    state: string;
    vipBrokerage: string;
    vipType: string;
    zipCode: string;
  };
  labels: {
    address1: string;
    address2: string;
    email: string;
    phoneNumber: string;
  };
  nameGrid: string;
  phone: {placeholder?: string; type?: 'tel'};
  streetGrid: string;
};

const VIP_FIELD_LAYOUTS: Record<VipFormMode, VipFieldLayout> = {
  create: {
    cityStateZipGrid: 'grid grid-cols-3 gap-6',
    contactGrid: 'grid grid-cols-2 gap-6',
    detailsGrid: 'grid grid-cols-2 gap-6',
    email: {placeholder: 'user@example.com'},
    identityGrid: 'grid grid-cols-2 gap-6',
    ids: {
      address1: 'vip-address1',
      address2: 'vip-address2',
      city: 'vip-city',
      dateOfBirth: 'vip-dob',
      email: 'vip-email',
      firstName: 'vip-first-name',
      gender: 'vip-gender',
      lastName: 'vip-last-name',
      phoneNumber: 'vip-phone',
      state: 'vip-state',
      vipBrokerage: 'vip-brokerage',
      vipType: 'vip-type',
      zipCode: 'vip-zip',
    },
    labels: {
      address1: 'Address',
      address2: 'Address Line 2 (optional)',
      email: 'Email Address',
      phoneNumber: 'Phone Number',
    },
    nameGrid: 'grid grid-cols-2 gap-6',
    phone: {placeholder: '5555555555'},
    streetGrid: '',
  },
  edit: {
    cityStateZipGrid: 'grid grid-cols-2 md:grid-cols-4 gap-4',
    contactGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    detailsGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    email: {},
    identityGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    ids: {
      address1: 'vip-edit-addr1',
      address2: 'vip-edit-addr2',
      city: 'vip-edit-city',
      dateOfBirth: 'vip-edit-dob',
      email: 'vip-edit-email',
      firstName: 'vip-edit-first',
      gender: 'vip-edit-gender',
      lastName: 'vip-edit-last',
      phoneNumber: 'vip-edit-phone',
      state: 'vip-edit-state',
      vipBrokerage: 'vip-edit-brokerage',
      vipType: 'vip-edit-type',
      zipCode: 'vip-edit-zip',
    },
    labels: {
      address1: 'Address Line 1',
      address2: 'Address Line 2',
      email: 'Email',
      phoneNumber: 'Phone',
    },
    nameGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
    phone: {type: 'tel'},
    streetGrid: 'grid grid-cols-1 md:grid-cols-2 gap-4',
  },
};

function VipTextField({
  id,
  label,
  onChange,
  placeholder,
  type,
  value,
}: {
  id: string;
  label: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  value: string;
}) {
  return (
    <div className='space-y-2'>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

export function VipNameFields({onChange, values, variant}: VipFieldBlockProps) {
  const layout = VIP_FIELD_LAYOUTS[variant];
  return (
    <div className={layout.nameGrid}>
      <VipTextField
        id={layout.ids.firstName}
        label='First Name'
        value={values.firstName}
        onChange={(firstName) => onChange({firstName})}
      />
      <VipTextField
        id={layout.ids.lastName}
        label='Last Name'
        value={values.lastName}
        onChange={(lastName) => onChange({lastName})}
      />
    </div>
  );
}

export function VipContactFields({onChange, values, variant}: VipFieldBlockProps) {
  const layout = VIP_FIELD_LAYOUTS[variant];
  return (
    <div className={layout.contactGrid}>
      <VipTextField
        id={layout.ids.email}
        label={layout.labels.email}
        type='email'
        placeholder={layout.email.placeholder}
        value={values.email}
        onChange={(email) => onChange({email})}
      />
      <VipTextField
        id={layout.ids.phoneNumber}
        label={layout.labels.phoneNumber}
        type={layout.phone.type}
        placeholder={layout.phone.placeholder}
        value={values.phoneNumber}
        onChange={(phoneNumber) => onChange({phoneNumber})}
      />
    </div>
  );
}

/** Date of birth + gender; create shows DOB first, edit leads with gender. */
export function VipIdentityFields({onChange, values, variant}: VipFieldBlockProps) {
  const layout = VIP_FIELD_LAYOUTS[variant];
  const dateOfBirthField = (
    <VipTextField
      id={layout.ids.dateOfBirth}
      label='Date of Birth'
      type='date'
      value={values.dateOfBirth}
      onChange={(dateOfBirth) => onChange({dateOfBirth})}
    />
  );
  const genderField = (
    <div className='space-y-2'>
      <Label htmlFor={layout.ids.gender}>Gender</Label>
      <GenderSelect
        id={layout.ids.gender}
        value={values.gender}
        onValueChange={(gender) => onChange({gender})}
      />
    </div>
  );
  return (
    <div className={layout.identityGrid}>
      {variant === 'create' ? (
        <>
          {dateOfBirthField}
          {genderField}
        </>
      ) : (
        <>
          {genderField}
          {dateOfBirthField}
        </>
      )}
    </div>
  );
}

/**
 * Street address, city, state and zip. Create renders the street lines as siblings of the form's
 * vertical stack; edit nests them in an address section with its own spacing.
 */
export function VipAddressFields({onChange, values, variant}: VipFieldBlockProps) {
  const layout = VIP_FIELD_LAYOUTS[variant];
  const address1Field = (
    <VipTextField
      id={layout.ids.address1}
      label={layout.labels.address1}
      value={values.address1}
      onChange={(address1) => onChange({address1})}
    />
  );
  const address2Field = (
    <VipTextField
      id={layout.ids.address2}
      label={layout.labels.address2}
      value={values.address2}
      onChange={(address2) => onChange({address2})}
    />
  );
  const cityStateZip = (
    <div className={layout.cityStateZipGrid}>
      <VipTextField
        id={layout.ids.city}
        label='City'
        value={values.city}
        onChange={(city) => onChange({city})}
      />
      <div className='space-y-2'>
        <Label htmlFor={layout.ids.state}>State</Label>
        <StateSelect
          id={layout.ids.state}
          value={values.state}
          onValueChange={(state) => onChange({state})}
        />
      </div>
      <VipTextField
        id={layout.ids.zipCode}
        label='Zip Code'
        value={values.zipCode}
        onChange={(zipCode) => onChange({zipCode})}
      />
    </div>
  );

  if (variant === 'create') {
    return (
      <>
        {address1Field}
        {address2Field}
        {cityStateZip}
      </>
    );
  }

  return (
    <div className='space-y-4'>
      <div className={layout.streetGrid}>
        {address1Field}
        {address2Field}
      </div>
      {cityStateZip}
    </div>
  );
}

export function VipTypeFields({
  onChange,
  values,
  variant,
  vipTypeOptions,
}: VipFieldBlockProps & {vipTypeOptions: readonly VipType[]}) {
  const layout = VIP_FIELD_LAYOUTS[variant];
  return (
    <div className={layout.detailsGrid}>
      <div className='space-y-2'>
        <Label htmlFor={layout.ids.vipType}>VIP Type</Label>
        <Select
          value={values.vipType || undefined}
          onValueChange={(vipType) => onChange({vipType: vipType as VipType})}
        >
          <SelectTrigger id={layout.ids.vipType}>
            <SelectValue placeholder='Select VIP type' />
          </SelectTrigger>
          <SelectContent>
            {vipTypeOptions.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {VIP_TYPE_LABELS[opt]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <VipTextField
        id={layout.ids.vipBrokerage}
        label='Group / Brokerage'
        value={values.vipBrokerage}
        onChange={(vipBrokerage) => onChange({vipBrokerage})}
      />
    </div>
  );
}
